/** @typedef {'legacy' | 'corridor'} DesignThemeId */

export const DESIGN_THEMES = {
  LEGACY: 'legacy',
  CORRIDOR: 'corridor',
};

export const DESIGN_THEME_STORAGE_KEY = 'swarm-design-theme';
export const DESIGN_THEME_URL_PARAM = 'design';

/** Corridor theme — aligned with SWARM logo (#ce3c2a) */
export const CORRIDOR_VARS = {
  '--primary-color': '#ce3c2a',
  '--primary-500': '#ce3c2a',
  '--primary-600': '#b53424',
  '--primary-700': '#9a2d1f',
  '--primary-800': '#7a2419',
  '--secondary-color': '#f7f5f4',
  '--accent-color': '#ce3c2a',
  '--header-bg': '#ffffff',
  '--header-text': '#2d2d2d',
  '--footer-bg': '#f7f5f4',
  '--footer-text': '#2d2d2d',
  '--button-primary-bg': '#ce3c2a',
  '--button-primary-text': '#ffffff',
  '--button-secondary-bg': '#f0eeec',
  '--button-secondary-text': '#2d2d2d',
  '--card-bg': '#ffffff',
  '--card-border': '#e8e4e1',
  '--font-family': '"Source Sans 3"',
  '--font-display': '"Source Sans 3"',
  '--font-mono': '"JetBrains Mono"',
  '--font-weights': '400,600,700',
  '--surface-footpath': '#f7f5f4',
  '--surface-asphalt': '#ffffff',
  '--line-marking': '#ce3c2a',
  '--bike-lane': '#2a9d8f',
  '--incident-pin': '#ce3c2a',
  '--text-secondary': '#6b6560',
  '--focus-ring': '#ce3c2a',
};

export const LEGACY_DEFAULT_VARS = {
  '--primary-color': '#1a237e',
  '--secondary-color': '#f5f7fa',
  '--accent-color': '#ff6b35',
  '--header-bg': '#212a3f',
  '--header-text': '#ffffff',
  '--footer-bg': '#f5f7fa',
  '--footer-text': '#212a3f',
  '--button-primary-bg': '#1a237e',
  '--button-primary-text': '#ffffff',
  '--button-secondary-bg': '#e0e7ef',
  '--button-secondary-text': '#1a237e',
  '--card-bg': '#ffffff',
  '--card-border': '#e0e7ef',
  '--font-family': 'Inter',
  '--font-weights': '400,500,600,700',
};

export const DESIGN_COPY = {
  legacy: {
    submitReport: 'Submit Incident Report',
    reportStatistics: 'Report Statistics',
    signInRegister: 'Sign In / Register',
    backToMain: '← Back to Main',
    incidentMap: 'Incident Map',
    recentReports: 'Recent Reports',
    optionalRegistrationTitle: 'Registration is Optional',
    optionalRegistrationBody:
      'Creating an account only helps pre-fill your details when submitting reports. None of your personal information will be visible to the public.',
    optionalRegistrationShort:
      'Registration is optional! It only helps pre-fill your details when submitting reports. None of your personal information will be visible to the public.',
    statisticsTitle: 'Report Statistics',
    statisticsSubtitle: 'Community Traffic Safety Data',
    welcomeSubtitle: 'Incident Reporter',
  },
  corridor: {
    submitReport: 'Report what you saw',
    reportStatistics: 'Charts & analysis',
    signInRegister: 'Save my details for next time',
    backToMain: '← Back to map',
    incidentMap: 'Murray Road corridor',
    recentReports: 'Recent reports',
    optionalRegistrationTitle: 'No account needed',
    optionalRegistrationBody:
      'Report without signing in. Your name and contact details stay private — only administrators see them.',
    optionalRegistrationShort: 'Report without an account. Your name stays private.',
    statisticsTitle: 'Incident analysis',
    statisticsSubtitle: 'Graphs and trends from community reports',
    statisticsStripCta: 'View charts & statistical analysis',
    statisticsStripDetail: 'Incident types, trends over time, and breakdowns',
    welcomeSubtitle: 'Safer Walking and Riding for Murray Rd',
  },
};

const VALID_THEMES = new Set(Object.values(DESIGN_THEMES));

export function isValidDesignTheme(value) {
  return VALID_THEMES.has(value);
}

export function getDesignCopy(themeId) {
  return DESIGN_COPY[themeId] || DESIGN_COPY.legacy;
}

export function brandingConfigToVars(brandingConfig) {
  const defaults = LEGACY_DEFAULT_VARS;
  if (!brandingConfig) {
    return { ...defaults };
  }
  return {
    '--primary-color': brandingConfig.primary_color || defaults['--primary-color'],
    '--secondary-color': brandingConfig.secondary_color || defaults['--secondary-color'],
    '--accent-color': brandingConfig.accent_color || defaults['--accent-color'],
    '--header-bg': brandingConfig.header_background || defaults['--header-bg'],
    '--header-text': brandingConfig.header_text_color || defaults['--header-text'],
    '--footer-bg': brandingConfig.footer_background || defaults['--footer-bg'],
    '--footer-text': brandingConfig.footer_text_color || defaults['--footer-text'],
    '--button-primary-bg': brandingConfig.button_primary_bg || defaults['--button-primary-bg'],
    '--button-primary-text':
      brandingConfig.button_primary_text || defaults['--button-primary-text'],
    '--button-secondary-bg':
      brandingConfig.button_secondary_bg || defaults['--button-secondary-bg'],
    '--button-secondary-text':
      brandingConfig.button_secondary_text || defaults['--button-secondary-text'],
    '--card-bg': brandingConfig.card_background || defaults['--card-bg'],
    '--card-border': brandingConfig.card_border_color || defaults['--card-border'],
    '--font-family': brandingConfig.font_family || defaults['--font-family'],
    '--font-weights': Array.isArray(brandingConfig.font_weights)
      ? brandingConfig.font_weights.join(',')
      : defaults['--font-weights'],
  };
}

export function resolveThemeVariables(themeId, brandingConfig) {
  if (themeId === DESIGN_THEMES.CORRIDOR) {
    return { ...CORRIDOR_VARS };
  }
  return brandingConfigToVars(brandingConfig);
}

export function readInitialDesignTheme() {
  if (typeof window !== 'undefined') {
    const fromUrl = new URLSearchParams(window.location.search).get(DESIGN_THEME_URL_PARAM);
    if (fromUrl && isValidDesignTheme(fromUrl)) {
      return fromUrl;
    }
    const fromStorage = localStorage.getItem(DESIGN_THEME_STORAGE_KEY);
    if (fromStorage && isValidDesignTheme(fromStorage)) {
      return fromStorage;
    }
  }
  const fromEnv = process.env.REACT_APP_DESIGN_THEME;
  if (fromEnv && isValidDesignTheme(fromEnv)) {
    return fromEnv;
  }
  return DESIGN_THEMES.LEGACY;
}

export function isDesignSwitcherEnabled() {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  return process.env.REACT_APP_ENABLE_DESIGN_SWITCHER === 'true';
}

const CORRIDOR_FONT_URL =
  'https://fonts.googleapis.com/css2?family=Barlow+Semi+Condensed:wght@600;700&family=JetBrains+Mono:wght@400;500&family=Source+Sans+3:wght@400;600&display=swap';

export function ensureCorridorFonts() {
  if (typeof document === 'undefined') {
    return;
  }
  if (document.getElementById('swarm-corridor-fonts')) {
    return;
  }
  const link = document.createElement('link');
  link.id = 'swarm-corridor-fonts';
  link.rel = 'stylesheet';
  link.href = CORRIDOR_FONT_URL;
  document.head.appendChild(link);
}

export function applyThemeVariables(themeId, brandingConfig) {
  const root = document.documentElement;
  const body = document.body;
  root.dataset.designTheme = themeId;

  const vars = resolveThemeVariables(themeId, brandingConfig);
  Object.entries(vars).forEach(([key, value]) => {
    if (value) {
      body.style.setProperty(key, value);
    }
  });

  const weights =
    themeId === DESIGN_THEMES.CORRIDOR
      ? ['700', '600', '400']
      : brandingConfig?.font_weights && Array.isArray(brandingConfig.font_weights)
        ? brandingConfig.font_weights
        : ['700', '600', '500', '400'];

  weights.forEach((weight, index) => {
    body.style.setProperty(`--font-weight-${index + 1}`, weight);
  });

  if (themeId === DESIGN_THEMES.CORRIDOR) {
    ensureCorridorFonts();
  }
}
